import { CustomFieldsDraft, MyShoppingListDraft } from "@commercetools/platform-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PrintProvider, ProductConfiguration } from "@wttb/product-configurator";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";

import { createApiClient } from "@/clients/commercetools";

interface CreateShoppingListInputType {
  name: string;
  sku: string;
  quantity?: number;
  config: ProductConfiguration;
  printProvider: PrintProvider;
}

const createShoppingList = async (session: Session | null, input: CreateShoppingListInputType) => {
  if (!session) return null;
  if (session.isAnonymous) return null;

  const api = createApiClient(session!.accessToken);

  const custom: CustomFieldsDraft = {
    type: {
      typeId: "type",
      key: "line-item-additional-fields",
    },
    fields: {
      productConfig: JSON.stringify(input.config),
      providerId: input.printProvider.id,
    },
  };
  const body: MyShoppingListDraft = {
    name: { en: input.name },
    lineItems: [
      {
        sku: input.sku,
        quantity: input?.config?.quantity as number,
        custom,
      },
    ],
  };

  const response = await api.me().shoppingLists().post({ body }).execute();
  return response.body;
};

export const useCreateShoppingListMutation = () => {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  return useMutation((input: CreateShoppingListInputType) => createShoppingList(session, input), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["SHOPPING_LIST"] });
    },
    onError: (data: Error) => {
      toast.error("Error creating a shopping list: " + data.message);
    },
  });
};
